import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { LogOut, Home, Download, TrendingUp, Users, Calendar, BarChart3, ArrowDown } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const BarChart = ({ data, maxVal, colorKey }) => {
  const max = maxVal || Math.max(...data.map(d => d.count), 1);
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3" data-testid={`bar-${i}`}>
          <span className="text-xs text-[#475569] w-32 truncate text-right">{item[colorKey] || item.source || item.status || item.service || 'Other'}</span>
          <div className="flex-grow h-7 bg-[#F1F5F9] rounded-sm overflow-hidden">
            <div className="h-full bg-[#1D4ED8] rounded-sm transition-all" style={{ width: `${(item.count / max * 100)}%`, minWidth: item.count > 0 ? '24px' : '0' }}>
              <span className="text-xs text-white px-2 leading-7">{item.count}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const FunnelChart = ({ funnel }) => {
  const maxCount = Math.max(...funnel.map(f => f.count), 1);
  return (
    <div className="space-y-2">
      {funnel.map((stage, i) => (
        <div key={i} className="flex items-center gap-3" data-testid={`funnel-stage-${i}`}>
          <span className="text-xs text-[#475569] w-40 text-right">{stage.stage}</span>
          <div className="flex-grow flex items-center gap-2">
            <div className="h-10 rounded-sm transition-all flex items-center" style={{ width: `${Math.max((stage.count / maxCount * 100), 5)}%`, backgroundColor: stage.color, minWidth: '40px' }}>
              <span className="text-sm text-white font-bold px-3">{stage.count}</span>
            </div>
            {i < funnel.length - 1 && stage.count > 0 && funnel[i+1].count > 0 && (
              <span className="text-xs text-[#94A3B8]">{Math.round(funnel[i+1].count / stage.count * 100)}%</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overRes, funnelRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/analytics/overview`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/analytics/funnel`, { withCredentials: true }),
        ]);
        setOverview(overRes.data);
        setFunnel(funnelRes.data);
      } catch { toast.error('Failed to load analytics'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const res = await axios.get(`${API_URL}/api/admin/export/${type}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} exported`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(null); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <AdminLayout title="Analytics"><div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0A0F1C]" data-testid="analytics-title">Analytics & Reporting</h1>
        </div>

        {/* KPI Cards */}
        {overview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {[
                { label: 'Total Leads', value: overview.totals.leads, sub: `+${overview.recent_30d.leads} this month`, icon: Users, color: 'text-[#1D4ED8]' },
                { label: 'Clients', value: overview.totals.clients, sub: `+${overview.recent_30d.clients} this month`, icon: Users, color: 'text-green-600' },
                { label: 'Conversion', value: `${overview.conversion_rate}%`, sub: 'Lead to Client', icon: TrendingUp, color: 'text-[#F97316]' },
                { label: 'Bookings', value: overview.totals.bookings, sub: `+${overview.recent_30d.bookings} this month`, icon: Calendar, color: 'text-purple-600' },
                { label: 'Enrollments', value: overview.totals.enrollments, sub: `${overview.totals.registrations} event regs`, icon: BarChart3, color: 'text-[#1D4ED8]' },
              ].map((kpi, i) => (
                <div key={i} className="bg-white p-5 border border-[#E2E8F0] rounded-lg" data-testid={`kpi-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <kpi.icon size={20} className={kpi.color} />
                  </div>
                  <div className="text-2xl font-bold text-[#0A0F1C]">{kpi.value}</div>
                  <div className="text-xs text-[#475569]">{kpi.label}</div>
                  <div className="text-xs text-[#94A3B8] mt-1">{kpi.sub}</div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              {/* Lead Sources */}
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <h2 className="font-bold text-[#0A0F1C] mb-4">Lead Sources</h2>
                {overview.lead_sources.length > 0 ? (
                  <BarChart data={overview.lead_sources} colorKey="source" />
                ) : <p className="text-[#475569] text-center py-4">No data yet</p>}
              </div>

              {/* Lead Statuses */}
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
                <h2 className="font-bold text-[#0A0F1C] mb-4">Lead Status Breakdown</h2>
                {overview.lead_statuses.length > 0 ? (
                  <BarChart data={overview.lead_statuses} colorKey="status" />
                ) : <p className="text-[#475569] text-center py-4">No data yet</p>}
              </div>
            </div>

            {/* Service Interests */}
            {overview.service_interests.length > 0 && (
              <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg mb-8">
                <h2 className="font-bold text-[#0A0F1C] mb-4">Top Service Interests</h2>
                <BarChart data={overview.service_interests} colorKey="service" />
              </div>
            )}
          </>
        )}

        {/* Funnel */}
        {funnel && (
          <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg mb-8">
            <h2 className="font-bold text-[#0A0F1C] mb-4" data-testid="funnel-title">Conversion Funnel</h2>
            <FunnelChart funnel={funnel.funnel} />
          </div>
        )}

        {/* CSV Exports */}
        <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
          <h2 className="font-bold text-[#0A0F1C] mb-4" data-testid="exports-title">Data Exports</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {['leads', 'clients', 'bookings', 'enrollments', 'registrations'].map(type => (
              <Button
                key={type}
                onClick={() => handleExport(type)}
                disabled={exporting === type}
                className="btn-outline flex items-center justify-center gap-2 capitalize"
                data-testid={`export-${type}`}
              >
                <Download size={14} /> {exporting === type ? 'Exporting...' : type}
              </Button>
            ))}
          </div>
        </div>
      </div></AdminLayout>);}
